import React, { useState } from "react";
import { toast } from "sonner";
import { Ticket, Send, Pencil, Trash2, Save, X } from "lucide-react";
import useFetch from "@/lib/useFetch";
import api from "@/lib/api";
import { PageHeader, Footer, SectionCard } from "@/components/common/Page";
import Loader from "@/components/common/Loader";
import StatusBadge from "@/components/common/StatusBadge";
import ConfirmButton from "@/components/common/ConfirmButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { timeAgo } from "@/lib/communities";

const CATEGORIES = ["General", "Player Report", "Ban Appeal", "Server Issue", "Kit Issue", "Payment Issue", "Bug Report"];

const MACROS = [
    { label: "Acknowledge", text: "Thanks for reaching out — we've received your ticket and a staff member is looking into it now." },
    { label: "Need Info", text: "Could you share more detail (server, time, screenshots, player name)? That'll help us resolve this faster." },
    { label: "Resolved", text: "This has been resolved. Open a new ticket if anything else comes up — happy gaming!" },
    { label: "Appeal Denied", text: "After review, your appeal has been denied. The ban stands per our server rules." },
    { label: "Credit Issued", text: "A credit has been applied to your account. Allow a few minutes for it to appear in your wallet." },
];

export default function Tickets() {
    const { data, reload } = useFetch("/admin/tickets");
    const [active, setActive] = useState(null);
    const [reply, setReply] = useState("");
    const [editing, setEditing] = useState(false);
    const [edit, setEdit] = useState({});
    if (!data) return <Loader />;

    const setStatus = async (t, status) => {
        await api.patch(`/admin/tickets/${t.id}/status`, { status });
        toast.success(`Ticket ${status.replace("_", " ")}`); reload(); setActive({ ...t, status });
    };
    const sendReply = async (t) => {
        if (!reply.trim()) return;
        const { data: updated } = await api.post(`/tickets/${t.id}/reply`, { message: reply });
        setReply(""); setActive(updated); reload();
    };
    const startEdit = () => { setEdit({ subject: active.subject, category: active.category, message: active.message }); setEditing(true); };
    const saveEdit = async () => {
        const { data: updated } = await api.patch(`/admin/tickets/${active.id}`, edit);
        toast.success("Ticket updated"); setActive(updated); setEditing(false); reload();
    };
    const removeTicket = async (t) => {
        await api.delete(`/admin/tickets/${t.id}`);
        toast.success("Ticket deleted"); setActive(null); reload();
    };
    const resolveClose = async (t) => {
        if (reply.trim()) await api.post(`/tickets/${t.id}/reply`, { message: reply });
        await api.patch(`/admin/tickets/${t.id}/status`, { status: "closed" });
        setReply(""); toast.success("Ticket resolved & closed"); reload(); setActive({ ...t, status: "closed" });
    };

    return (
        <div data-testid="admin-tickets">
            <PageHeader title="Support Tickets" subtitle="Respond to, edit, resolve or remove player reports, appeals and issues." />
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-5">
                <div className="space-y-2">
                    {data.length === 0 && <div className="text-muted-foreground">No tickets open.</div>}
                    {data.map((t) => (
                        <button key={t.id} data-testid={`admin-ticket-${t.id}`} onClick={() => { setActive(t); setEditing(false); }}
                            className={`w-full text-left tactical-card rounded-md p-3 ${active?.id === t.id ? "border-burnt/50" : ""}`}>
                            <div className="flex items-center justify-between">
                                <span className="font-heading font-semibold uppercase text-foreground">{t.subject}</span>
                                <StatusBadge status={t.status === "closed" ? "offline" : t.status === "open" ? "warning" : "active"} label={t.status.replace("_", " ").toUpperCase()} />
                            </div>
                            <div className="label-mono text-muted-foreground mt-1">{t.username} · {t.category} · {timeAgo(t.created_at)}</div>
                        </button>
                    ))}
                </div>
                {active && (
                    <SectionCard title={editing ? "Edit Ticket" : active.subject} testId="admin-ticket-detail"
                        action={
                            <div className="flex items-center gap-1.5">
                                {!editing ? (
                                    <>
                                        <Select value={active.status} onValueChange={(v) => setStatus(active, v)}>
                                            <SelectTrigger data-testid="ticket-status-select" className="w-36 bg-background border-border h-8"><SelectValue /></SelectTrigger>
                                            <SelectContent>{["open", "in_progress", "closed"].map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                                        </Select>
                                        <Button data-testid="ticket-edit" size="icon" variant="outline" className="h-8 w-8 border-border hover:border-burnt/50 hover:text-burnt" onClick={startEdit}><Pencil size={14} /></Button>
                                        <ConfirmButton testId="ticket-delete" confirmTestId="ticket-delete-confirm" size="icon" variant="outline"
                                            className="h-8 w-8 border-danger/40 text-danger hover:bg-danger/10"
                                            onConfirm={() => removeTicket(active)}
                                            title="Delete this ticket?" description="This permanently removes the ticket and all its replies. This cannot be undone." confirmLabel="Delete">
                                            <Trash2 size={14} />
                                        </ConfirmButton>
                                    </>
                                ) : (
                                    <>
                                        <Button data-testid="ticket-edit-save" size="icon" className="h-8 w-8 bg-radioactive/90 text-black hover:bg-radioactive" onClick={saveEdit}><Save size={14} /></Button>
                                        <Button data-testid="ticket-edit-cancel" size="icon" variant="outline" className="h-8 w-8 border-border" onClick={() => setEditing(false)}><X size={14} /></Button>
                                    </>
                                )}
                            </div>
                        }>
                        {editing ? (
                            <div className="space-y-3">
                                <div>
                                    <div className="label-mono text-muted-foreground mb-1">Subject</div>
                                    <Input data-testid="ticket-edit-subject" value={edit.subject} onChange={(e) => setEdit({ ...edit, subject: e.target.value })} className="bg-background border-border" />
                                </div>
                                <div>
                                    <div className="label-mono text-muted-foreground mb-1">Category</div>
                                    <Select value={edit.category} onValueChange={(v) => setEdit({ ...edit, category: v })}>
                                        <SelectTrigger data-testid="ticket-edit-category" className="bg-background border-border"><SelectValue /></SelectTrigger>
                                        <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <div className="label-mono text-muted-foreground mb-1">Message</div>
                                    <Textarea data-testid="ticket-edit-message" rows={4} value={edit.message} onChange={(e) => setEdit({ ...edit, message: e.target.value })} className="bg-background border-border" />
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="flex items-center gap-2 mb-3"><Ticket size={15} className="text-burnt" /><span className="label-mono text-muted-foreground">{active.category} · {active.username}</span></div>
                                <p className="text-sm text-foreground rounded-md border border-border bg-background/40 p-3 whitespace-pre-wrap">{active.message}</p>
                                <div className="space-y-2 mt-3 max-h-64 overflow-y-auto">
                                    {(active.replies || []).map((r, i) => (
                                        <div key={`${r.created_at}-${i}`} className={`rounded-md border p-2.5 ${r.is_staff ? "border-burnt/40 bg-burnt/10" : "border-border bg-background/40"}`}>
                                            <div className="label-mono text-muted-foreground">{r.author} {r.is_staff ? "· STAFF" : ""} · {timeAgo(r.created_at)}</div>
                                            <p className="text-sm text-foreground mt-0.5">{r.message}</p>
                                        </div>
                                    ))}
                                </div>
                                {active.status !== "closed" && (
                                    <div className="mt-3">
                                        <div className="flex flex-wrap gap-1.5 mb-2">
                                            {MACROS.map((m) => (
                                                <button key={m.label} data-testid={`macro-${m.label.toLowerCase().replace(/\s+/g, "-")}`}
                                                    onClick={() => setReply(m.text)}
                                                    className="rounded-sm border border-border px-2 py-1 label-mono text-muted-foreground hover:border-burnt/50 hover:text-burnt transition-colors">
                                                    {m.label}
                                                </button>
                                            ))}
                                        </div>
                                        <div className="flex gap-2">
                                            <Input data-testid="admin-reply-input" value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Staff reply…" className="bg-background border-border" />
                                            <Button data-testid="admin-reply-send" onClick={() => sendReply(active)} className="bg-burnt text-white hover:bg-burnt/90"><Send size={15} /></Button>
                                            <Button data-testid="ticket-resolve-close" onClick={() => resolveClose(active)} className="bg-radioactive/90 text-black hover:bg-radioactive font-heading uppercase whitespace-nowrap">Send &amp; Close</Button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </SectionCard>
                )}
            </div>
            <Footer />
        </div>
    );
}
